import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useDueReminder, alarmLevel } from "@/hooks/useDueReminder";
import { AlarmHeader, AlarmControls, alarmStyle } from "@/components/alarm/AlarmParts";
import { ModalClose } from "@/components/ui/ModalClose";

export function AlarmOverlay({ onCheckIn }) {
  const [event, , hide] = useDueReminder();

  const snooze = async () => {
    try {
      const r = await api.post(`/reminders/events/${event.id}/snooze`);
      if (!r.data.allowed) return toast(r.data.message);
      const left = r.data.snoozes_left;
      toast(`Snoozed ${r.data.minutes} min. ${left > 0 ? `${left} snooze${left === 1 ? "" : "s"} left.` : "That was the last snooze."}`);
    } catch { toast("Couldn't snooze — hiding it for now."); }
    hide(event.id);
  };

  const dismiss = async () => {
    try { await api.post(`/reminders/events/${event.id}/dismiss`); toast("Dismissed for today. Tomorrow is a fresh start."); }
    catch { toast("Dismissed on this device."); }
    hide(event.id);
  };

  const done = async () => {
    try { await api.post(`/reminders/events/${event.id}/done`); } catch { /* still let the user through */ }
    hide(event.id);
    onCheckIn?.(event.habit_id);
  };

  if (!event) return null;
  const level = alarmLevel(event);
  const s = alarmStyle(level);

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] flex items-center justify-center bg-stone-900/60 backdrop-blur-sm p-4" data-testid="alarm-overlay">
        <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }}
          className={`relative w-full max-w-md rounded-3xl p-8 ${s.bg} ring-4 ${s.ring} shadow-2xl space-y-5`} data-testid={`alarm-level-${level}`}>
          <ModalClose onClose={dismiss} testid="alarm-close" label="Dismiss for today" />
          <AlarmHeader event={event} level={level} />
          <AlarmControls event={event} onDone={done} onSnooze={snooze} onDismiss={dismiss} />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
